/**
 * RadialDial — the basemap switcher, Design D from BisaWofa-LayerSwitcher-Designs.
 *
 * A circular button sits in the bottom corner of the map. Tapping it fans a
 * quarter-circle of basemap options up and inward. The user can either tap an
 * option directly, or press the dial and sweep a thumb around the arc — the
 * option under the thumb highlights and its name appears in a readout, and
 * releasing selects it. Tapping outside, or Escape, closes without changing
 * anything.
 *
 * Design notes:
 *   - Options carry the real tile previews (the same /img/*.webp screenshots
 *     the bottom-sheet picker uses) rather than icons, so the choice is made
 *     by looking at maps rather than reading names.
 *   - Only the highlighted option is named, in a readout beside the dial. With
 *     seven basemaps the circles sit close together, and a label under each
 *     one would collide; naming just the highlighted option is also what makes
 *     the sweep gesture legible ("this is the one I am about to pick").
 *   - The active basemap keeps a purple ring and a purple wash on its arc
 *     segment, so the current choice is visible the moment the dial opens.
 *
 * Accessibility (the design paper flagged this as its weakest point):
 *   - every option is a real <button> with an aria-label, so the sweep gesture
 *     is an enhancement rather than the only way in;
 *   - the dial is a `menu`, arrow keys move the highlight, Enter/Space selects,
 *     Escape closes, and focus returns to the dial button;
 *   - `prefers-reduced-motion` drops the staggered fan-out.
 *
 * Usage:
 *   const dial = new RadialDial(mapView, { target: mapEl });
 *   dial.open();  dial.close();  dial.destroy();
 *
 * @module components/RadialDial
 */

import { STORAGE_KEY } from './LayerPicker.js';

const B = import.meta.env.BASE_URL;

// Tile previews, keyed by the layer `title` set in MapView.createBaseLayers().
const PREVIEWS = {
  'OpenStreetMap': `${B}img/OSM.webp`,
  'Carto Light':   `${B}img/Light.webp`,
  'Carto Dark':    `${B}img/Dark.webp`,
  'Satellite':     `${B}img/Satellite.webp`,
  'Topographic':   `${B}img/Topography.webp`,
  'OSM Cycle map': `${B}img/OSMCycle.webp`,
  'Google Sat':    `${B}img/GoogleSat.webp`,
};

// Short names for the readout.
const DISPLAY_NAMES = {
  'OpenStreetMap': 'OSM',
  'OSM Cycle map': 'OSM Cycle',
  'Carto Light':   'Light',
  'Carto Dark':    'Dark',
};

// Preferred order along the arc, nearest the thumb first. Layers not listed
// keep their map order and follow these.
const ORDER = [
  'OpenStreetMap', 'Satellite', 'Topographic',
  'Carto Light', 'Carto Dark', 'OSM Cycle map', 'Google Sat',
];

const RADIUS = 190;      // px from dial centre to option centre
const FAB_SIZE = 56;
const ITEM_SIZE = 44;
const SWEEP = 90;        // degrees — a quarter circle, per the design paper

export class RadialDial {
  /**
   * @param {object} mapView            MapView instance (must expose getMap())
   * @param {object} [opts]
   * @param {HTMLElement} [opts.target] element to render into (default: map target)
   */
  constructor(mapView, opts = {}) {
    this.mapView = mapView;
    this.target = opts.target || mapView.getMap().getTargetElement();
    this.open_ = false;
    this.items = [];        // [{ title, displayTitle, layer, el, angle }]
    this.highlight = -1;    // index currently highlighted, -1 = none
    _ensureStyles();
    this._build();
  }

  /** Which corner the dial lives in. Mirrors with the left-handed setting. */
  get side() {
    return document.body.classList.contains('bw-left-handed') ? 'left' : 'right';
  }

  // ---------- construction ----------

  _build() {
    const root = document.createElement('div');
    root.className = 'rdial';
    root.innerHTML = `
      <div class="rdial-backdrop"></div>
      <svg class="rdial-arc" aria-hidden="true"></svg>
      <div class="rdial-items" role="menu" aria-label="Choose a map style"></div>
      <div class="rdial-readout" aria-live="polite"></div>
      <button type="button" class="rdial-fab" aria-haspopup="menu" aria-expanded="false"
              aria-label="Map style">
        <i class="bi bi-stack rdial-fab-open"></i>
        <i class="bi bi-x-lg rdial-fab-close"></i>
      </button>
    `;
    this.target.appendChild(root);
    this.root = root;
    // The corner must be right from the moment the dial appears — not only
    // once it is opened, which would make the button jump on first tap.
    root.classList.toggle('rdial-left', this.side === 'left');
    this.fab = root.querySelector('.rdial-fab');
    this.backdrop = root.querySelector('.rdial-backdrop');
    this.itemsWrap = root.querySelector('.rdial-items');
    this.readout = root.querySelector('.rdial-readout');
    this.arcSvg = root.querySelector('.rdial-arc');

    // A pointerdown on the dial opens it (and may begin a sweep). The browser
    // then fires a click for the same press, which would immediately toggle
    // the dial shut again — so a pointer-driven press suppresses that click.
    // Keyboard activation produces a click with no preceding pointerdown and
    // therefore still works.
    this._suppressClick = false;
    this.fab.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this._suppressClick) { this._suppressClick = false; return; }
      this.toggle();
    });
    this.backdrop.addEventListener('click', () => this.close());

    // Sweep gesture: press anywhere on the dial and drag around the arc.
    this.fab.addEventListener('pointerdown', (e) => this._onSweepStart(e));
    this._onKey = (e) => this._handleKey(e);
    document.addEventListener('keydown', this._onKey);
  }

  /** Read the basemaps out of the map, ordered for the arc. */
  _collectBases() {
    const map = this.mapView.getMap();
    const found = [];
    map.getLayers().forEach((layer) => {
      if (typeof layer.getLayers !== 'function') return;
      if (!(layer.get('title') || '').toLowerCase().includes('base')) return;
      layer.getLayers().forEach((child) => {
        const title = child.get('title') || 'Untitled';
        found.push({ title, displayTitle: DISPLAY_NAMES[title] || title, layer: child });
      });
    });
    // Preferred order first, then anything else in map order.
    const rank = (t) => { const i = ORDER.indexOf(t); return i === -1 ? ORDER.length : i; };
    return found.sort((a, b) => rank(a.title) - rank(b.title));
  }

  /** Angle (degrees, CCW from +x) for item i of n. */
  _angleFor(i, n) {
    // Right-handed: the arc sweeps from straight up (90°) to straight left
    // (180°). Left-handed mirrors it to sweep up and to the right.
    const a0 = 90;
    const dir = this.side === 'left' ? -1 : 1;
    return n <= 1 ? a0 + dir * SWEEP / 2 : a0 + dir * SWEEP * (i / (n - 1));
  }

  _renderItems() {
    const bases = this._collectBases();
    this.itemsWrap.innerHTML = '';
    this.items = [];

    const n = bases.length;
    bases.forEach((b, i) => {
      const angle = this._angleFor(i, n);
      const rad = (angle * Math.PI) / 180;
      const dx = RADIUS * Math.cos(rad);
      const dy = -RADIUS * Math.sin(rad);   // screen y grows downward

      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'rdial-item';
      el.setAttribute('role', 'menuitemradio');
      el.setAttribute('aria-label', b.displayTitle);
      el.style.setProperty('--tx', `${dx}px`);
      el.style.setProperty('--ty', `${dy}px`);
      el.style.setProperty('--delay', `${i * 26}ms`);

      const preview = PREVIEWS[b.title];
      el.innerHTML = preview
        ? `<span class="rdial-item-thumb" style="background-image:url('${preview}')"></span>`
        : `<span class="rdial-item-thumb rdial-item-thumb-fallback"></span>`;

      el.addEventListener('click', (e) => { e.stopPropagation(); this._select(i); });
      el.addEventListener('pointerenter', () => this._setHighlight(i));

      this.itemsWrap.appendChild(el);
      this.items.push({ ...b, el, angle });
    });

    this._reflectActive();
  }

  /** Mark the visible basemap as checked, and prime the highlight on it. */
  _reflectActive() {
    let activeIdx = -1;
    this.items.forEach((it, i) => {
      const on = it.layer.getVisible();
      it.el.classList.toggle('rdial-item-active', on);
      it.el.setAttribute('aria-checked', on ? 'true' : 'false');
      if (on) activeIdx = i;
    });
    this._setHighlight(activeIdx);
    this._drawArc(activeIdx);
  }

  // ---------- arc wash ----------

  /**
   * Draw the arc band behind the options, with the highlighted segment washed
   * in brand purple. Purely decorative — hidden from assistive tech.
   */
  _drawArc(highlightIdx) {
    const n = this.items.length;
    if (!n) { this.arcSvg.innerHTML = ''; return; }

    const S = RADIUS + ITEM_SIZE;      // svg is a square of this side
    const cx = this.side === 'left' ? 0 : S;
    const cy = S;
    this.arcSvg.setAttribute('viewBox', `0 0 ${S} ${S}`);
    this.arcSvg.style.width = `${S}px`;
    this.arcSvg.style.height = `${S}px`;

    const pt = (r, deg) => {
      const rad = (deg * Math.PI) / 180;
      return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
    };
    const band = (a, b, rIn, rOut) => {
      const [x1, y1] = pt(rOut, a), [x2, y2] = pt(rOut, b);
      const [x3, y3] = pt(rIn, b),  [x4, y4] = pt(rIn, a);
      // We always traverse the outer edge in the direction of increasing angle,
      // which is counter-clockwise on screen (SVG y grows downward) — sweep 0 —
      // and return along the inner edge the other way, sweep 1. This does not
      // depend on which corner the dial is docked in.
      return `M ${x1} ${y1} A ${rOut} ${rOut} 0 0 0 ${x2} ${y2} `
           + `L ${x3} ${y3} A ${rIn} ${rIn} 0 0 1 ${x4} ${y4} Z`;
    };

    const a0 = this._angleFor(0, n);
    const aN = this._angleFor(n - 1, n);
    const lo = Math.min(a0, aN), hi = Math.max(a0, aN);
    const rIn = RADIUS - 30, rOut = RADIUS + 30;

    let html = `<path d="${band(lo, hi, rIn, rOut)}" fill="rgba(139,0,139,0.07)"/>`;
    if (highlightIdx >= 0 && this.items[highlightIdx]) {
      const step = n > 1 ? Math.abs(aN - a0) / (n - 1) : SWEEP;
      const c = this.items[highlightIdx].angle;
      const s = Math.max(lo, c - step / 2);
      const e = Math.min(hi, c + step / 2);
      html += `<path d="${band(s, e, rIn, rOut)}" fill="rgba(139,0,139,0.20)"/>`;
    }
    this.arcSvg.innerHTML = html;
  }

  // ---------- highlight + selection ----------

  _setHighlight(i) {
    this.highlight = i;
    this.items.forEach((it, idx) => it.el.classList.toggle('rdial-item-hot', idx === i));
    const name = i >= 0 && this.items[i] ? this.items[i].displayTitle : '';
    this.readout.textContent = name;
    this.readout.classList.toggle('rdial-readout-on', !!name && this.open_);
    this._drawArc(i);
  }

  _select(i) {
    const chosen = this.items[i];
    if (!chosen) return;
    this.items.forEach((it) => it.layer.setVisible(it === chosen));
    try { localStorage.setItem(STORAGE_KEY, chosen.title); } catch (_) {}
    this._reflectActive();
    this.close();
  }

  // ---------- sweep gesture ----------

  _onSweepStart(e) {
    // A sweep only makes sense once the options are showing; the first press
    // opens the dial, and the same press can then continue into a sweep.
    this._suppressClick = true;
    const openedByThisPress = !this.open_;
    if (openedByThisPress) this.openMenu();
    const rect = this.fab.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let swept = false;

    const move = (ev) => {
      const dx = ev.clientX - cx;
      const dy = ev.clientY - cy;
      const dist = Math.hypot(dx, dy);
      // Ignore tiny movements so a plain tap doesn't count as a sweep.
      if (dist < FAB_SIZE / 2) return;
      swept = true;
      let deg = (Math.atan2(-dy, dx) * 180) / Math.PI;
      if (deg < 0) deg += 360;
      this._setHighlight(this._indexForAngle(deg));
    };
    const wasOpen = this.open_ && !openedByThisPress;
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (swept && this.highlight >= 0) {
        this._select(this.highlight);        // released on an option → pick it
      } else if (!swept && wasOpen) {
        this.close();                        // plain tap on an open dial → close
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }

  /** Nearest option to a pointer angle, or -1 if the pointer is off the arc. */
  _indexForAngle(deg) {
    const n = this.items.length;
    if (!n) return -1;
    const a0 = this._angleFor(0, n);
    const aN = this._angleFor(n - 1, n);
    const lo = Math.min(a0, aN), hi = Math.max(a0, aN);
    // A little tolerance past each end so the extremes stay easy to reach.
    const step = n > 1 ? Math.abs(aN - a0) / (n - 1) : SWEEP;
    if (deg < lo - step || deg > hi + step) return -1;
    const clamped = Math.min(hi, Math.max(lo, deg));
    const frac = (clamped - a0) / (aN - a0 || 1);
    return Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1))));
  }

  // ---------- keyboard ----------

  _handleKey(e) {
    if (!this.open_) return;
    const n = this.items.length;
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (!n) return;

    // Along the arc: "next" moves away from the thumb, whichever side we're on.
    if (['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
      const fwd = (e.key === 'ArrowUp' || e.key === (this.side === 'left' ? 'ArrowRight' : 'ArrowLeft'));
      let i = this.highlight;
      if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = n - 1;
      else i = i < 0 ? 0 : (i + (fwd ? 1 : -1) + n) % n;
      this._setHighlight(i);
      this.items[i]?.el.focus();
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && this.highlight >= 0) {
      e.preventDefault();
      this._select(this.highlight);
    }
  }

  // ---------- open / close ----------

  /**
   * Re-read the handedness setting and move the dial to the matching corner.
   * Call this when the left-handed setting is toggled. If the dial happens to
   * be open, the options are laid out again, since their offsets are mirrored.
   */
  refreshSide() {
    if (!this.root) return;
    this.root.classList.toggle('rdial-left', this.side === 'left');
    if (this.open_) this._renderItems();
  }

  toggle() { this.open_ ? this.close() : this.openMenu(); }

  openMenu() {
    if (this.open_) return;
    this._renderItems();
    this.open_ = true;
    this.root.classList.add('rdial-open');
    this.root.classList.toggle('rdial-left', this.side === 'left');   // defensive
    this.fab.setAttribute('aria-expanded', 'true');
    this.readout.classList.toggle('rdial-readout-on', this.highlight >= 0);
  }

  close() {
    if (!this.open_) return;
    this.open_ = false;
    this.root.classList.remove('rdial-open');
    this.fab.setAttribute('aria-expanded', 'false');
    this.readout.classList.remove('rdial-readout-on');
    this.fab.focus?.();
  }

  destroy() {
    document.removeEventListener('keydown', this._onKey);
    this.root?.remove();
  }
}

// ---------- styles ----------

let _styled = false;
function _ensureStyles() {
  if (_styled) return;
  _styled = true;
  const css = `
    .rdial { position: absolute; inset: 0; pointer-events: none; z-index: 690; }
    .rdial-backdrop {
      position: absolute; inset: 0; background: rgba(20,8,24,0.22);
      opacity: 0; pointer-events: none; transition: opacity 0.22s ease;
    }
    .rdial.rdial-open .rdial-backdrop { opacity: 1; pointer-events: auto; }

    /* Anchor: bottom-right by default, bottom-left when left-handed.
       'bottom' clears the dock. */
    .rdial-fab, .rdial-items, .rdial-arc, .rdial-readout {
      position: absolute; bottom: 84px; right: 16px;
    }
    .rdial.rdial-left .rdial-fab,
    .rdial.rdial-left .rdial-items,
    .rdial.rdial-left .rdial-arc,
    .rdial.rdial-left .rdial-readout { right: auto; left: 16px; }

    .rdial-fab {
      width: ${FAB_SIZE}px; height: ${FAB_SIZE}px; border: none; border-radius: 50%;
      background: linear-gradient(150deg, #8B008B, #a8259c); color: #fff;
      font-size: 21px; cursor: pointer; pointer-events: auto; touch-action: none;
      box-shadow: 0 4px 14px rgba(139,0,139,0.42);
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s;
      -webkit-tap-highlight-color: transparent;
    }
    .rdial-fab:hover { box-shadow: 0 6px 18px rgba(139,0,139,0.52); }
    .rdial-fab:focus-visible { outline: 3px solid #FFE96A; outline-offset: 3px; }
    .rdial .rdial-fab-close { display: none; }
    .rdial.rdial-open .rdial-fab { transform: rotate(90deg); }
    .rdial.rdial-open .rdial-fab .rdial-fab-open { display: none; }
    .rdial.rdial-open .rdial-fab .rdial-fab-close { display: inline; }

    /* Item origin sits at the FAB centre. */
    .rdial-items {
      width: ${FAB_SIZE}px; height: ${FAB_SIZE}px; pointer-events: none;
    }
    /* The arc svg draws its centre at its own bottom-right corner (bottom-left
       when left-handed), so that corner must sit exactly on the FAB centre:
       16px + half a FAB from the side, 84px + half a FAB from the bottom. */
    .rdial-arc {
      pointer-events: none; opacity: 0; transition: opacity 0.25s ease;
      right: ${16 + FAB_SIZE / 2}px; bottom: ${84 + FAB_SIZE / 2}px;
    }
    .rdial.rdial-left .rdial-arc {
      right: auto; left: ${16 + FAB_SIZE / 2}px; bottom: ${84 + FAB_SIZE / 2}px;
    }
    .rdial.rdial-open .rdial-arc { opacity: 1; }

    .rdial-item {
      position: absolute; left: 50%; top: 50%;
      width: ${ITEM_SIZE}px; height: ${ITEM_SIZE}px;
      margin-left: ${-ITEM_SIZE / 2}px; margin-top: ${-ITEM_SIZE / 2}px;
      padding: 0; border: none; background: transparent; cursor: pointer;
      transform: translate(0,0) scale(0.3); opacity: 0; pointer-events: none;
      transition: transform 0.3s cubic-bezier(0.22,1,0.36,1) var(--delay, 0ms),
                  opacity 0.2s ease var(--delay, 0ms);
      -webkit-tap-highlight-color: transparent;
    }
    .rdial.rdial-open .rdial-item {
      transform: translate(var(--tx), var(--ty)) scale(1);
      opacity: 1; pointer-events: auto;
    }
    .rdial-item-thumb {
      display: block; width: 100%; height: 100%; border-radius: 50%;
      background-size: cover; background-position: center;
      border: 2.5px solid #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      transition: border-color 0.15s, transform 0.15s;
    }
    .rdial-item-thumb-fallback { background: linear-gradient(135deg, #e0e0e0, #b0b0b0); }
    .rdial-item-active .rdial-item-thumb { border-color: #8B008B; }
    .rdial.rdial-open .rdial-item-hot { z-index: 2; }
    .rdial-item-hot .rdial-item-thumb {
      transform: scale(1.22); border-color: #FFE96A;
      box-shadow: 0 3px 14px rgba(0,0,0,0.4);
    }
    .rdial-item:focus-visible .rdial-item-thumb { outline: 3px solid #FFE96A; outline-offset: 2px; }

    /* Readout: names the highlighted style, beside the dial. */
    .rdial-readout {
      margin-bottom: ${FAB_SIZE + 12}px; margin-right: 2px;
      padding: 5px 13px; border-radius: 999px; white-space: nowrap;
      background: rgba(28,16,32,0.88); color: #fff;
      font-size: 12.5px; font-weight: 700; font-family: -apple-system, 'Segoe UI', sans-serif;
      opacity: 0; transform: translateY(4px); pointer-events: none;
      transition: opacity 0.18s ease, transform 0.18s ease;
    }
    .rdial-readout-on { opacity: 1; transform: translateY(0); }

    @media (prefers-reduced-motion: reduce) {
      .rdial-item, .rdial-fab, .rdial-arc, .rdial-readout { transition-duration: 0.01ms !important; }
      .rdial-item { transition-delay: 0ms !important; }
    }
  `;
  const el = document.createElement('style');
  el.id = 'radial-dial-styles';
  el.textContent = css;
  document.head.appendChild(el);
}

export default RadialDial;
